import { useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useCalendarEvents } from "@/components/bookings/useCalendarEvents";
import { sourceStyle, ymd, addDays, mondayOf, lastDay, SourceLegend } from "@/components/bookings/calendarUtils";

export function WeekTimeline() {
  const [weekStart, setWeekStart] = useState(() => mondayOf(new Date()));
  const days = Array.from({ length: 7 }, (_, i) => ymd(addDays(weekStart, i)));
  const { events, rooms, loading } = useCalendarEvents(days[0], days[6]);
  const visible = events.filter((e) => e.start <= days[6] && lastDay(e) >= days[0]);
  const extra = [...new Set(visible.map((e) => e.room).filter((r) => r && !rooms.includes(r)))];
  const rows = [...rooms, ...extra, ""];
  const today = ymd(new Date());

  return (
    <div className="space-y-5" data-testid="week-timeline">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <button onClick={() => setWeekStart(addDays(weekStart, -7))} data-testid="week-prev" className="p-2 rounded-md border border-border hover:bg-secondary"><ChevronLeft className="w-4 h-4" /></button>
          <h2 className="font-serif text-xl text-foreground min-w-[220px] text-center" data-testid="week-label">Week of {weekStart.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" })}</h2>
          <button onClick={() => setWeekStart(addDays(weekStart, 7))} data-testid="week-next" className="p-2 rounded-md border border-border hover:bg-secondary"><ChevronRight className="w-4 h-4" /></button>
          {loading && <span className="text-xs text-muted-foreground">Loading…</span>}
        </div>
        <SourceLegend />
      </div>
      <div className="overflow-x-auto border border-border rounded-md bg-card">
        <div className="min-w-[860px]">
          <div className="grid border-b border-border bg-secondary/40" style={{ gridTemplateColumns: "150px repeat(7, 1fr)" }}>
            <div className="px-3 py-2 text-[10px] uppercase tracking-widest text-muted-foreground">Room</div>
            {days.map((d) => (
              <div key={d} className={`px-2 py-2 text-xs border-l border-border ${d === today ? "text-foreground font-medium" : "text-muted-foreground"}`}>
                {new Date(`${d}T00:00:00`).toLocaleDateString(undefined, { weekday: "short", day: "numeric" })}
              </div>
            ))}
          </div>
          {rows.map((room) => {
            const evs = visible.filter((e) => (e.room || "") === room);
            if (room === "" && evs.length === 0) return null;
            return (
              <div key={room || "unassigned"} className="grid border-b border-border last:border-b-0" style={{ gridTemplateColumns: "150px repeat(7, 1fr)", gridAutoFlow: "row dense" }} data-testid={`timeline-row-${room || "unassigned"}`}>
                <div className="px-3 py-3 text-sm text-foreground border-r border-border" style={{ gridColumn: 1, gridRow: `1 / span ${Math.max(evs.length, 1)}` }}>{room || "Unassigned"}</div>
                {evs.length === 0 && <div style={{ gridColumn: "2 / 9" }} className="py-3" />}
                {evs.map((e) => {
                  const s = Math.max(days.indexOf(e.start < days[0] ? days[0] : e.start), 0);
                  const endD = lastDay(e) > days[6] ? days[6] : lastDay(e);
                  const en = Math.max(days.indexOf(endD), s);
                  return (
                    <div key={e.id} style={{ gridColumn: `${s + 2} / ${en + 3}` }} className="p-1" data-testid={`timeline-event-${e.id}`}>
                      <div className="rounded px-2 py-1 text-[11px] text-white truncate" style={{ backgroundColor: sourceStyle(e.source).color }} title={`${e.title} · ${e.start} → ${e.end}`}>
                        {e.time ? `${e.time} ` : ""}{e.guest || e.title}
                      </div>
                    </div>
                  );
                })}
              </div>
            );
          })}
          {rows.length === 1 && visible.length === 0 && <p className="text-sm text-muted-foreground p-6 text-center">No bookings this week.</p>}
        </div>
      </div>
    </div>
  );
}
